import React, { useEffect, useState } from 'react';
import {
    useForm,
    FormProvider,
    Controller,
    useFieldArray,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import {
    inputStyles,
    labelStyles,
    requiredStyles,
    sectionStyles,
} from '../styles';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { FaPlus } from 'react-icons/fa6';
import { RxCross2 } from 'react-icons/rx';
import { v4 as uuidv4 } from 'uuid';
import {
    fetchRoles,
    fetchDepartments,
    updateUser,
    createUser,
} from '~/services/users';
import { User } from '../../types/User';
import { PhoneNumberTypes, phoneNumberTypesList } from '~/enums/user';
import { useUserContext } from '~/context/UserProvider';
import Select from 'react-select';
import { startCase } from 'lodash';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    user?: User | null;
    onSuccess?: (user: User) => void;
}
type Contact = {
    phone: string;
    type: PhoneNumberTypes;
};
type Department = {
    name: string;
    role: string;
};
type DefaultValues = {
    first_name: string;
    middle_name: string;
    last_name: string;
    contacts: Contact[];
    email: string;
    roles: string[];
    departments: Department[];
};

type UserRoleType = {
    id: number;
    name: string;
};

const departmentRoles = ['Manager', 'Member'];

const defaultValues: DefaultValues = {
    first_name: '',
    middle_name: '',
    last_name: '',
    contacts: [],
    email: '',
    roles: [],
    departments: [],
};

const ErrorMessage = ({ message }: { message: string }) => (
    <p style={{ color: '#ff4b4b' }} className=' mt-1 text-sm'>
        {message}
    </p>
);

const AddUserDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    user,
    onSuccess,
}) => {
    // const { user, userDispatch } = useUserContext();
    const [roles, setRoles] = useState<UserRoleType[]>([]);
    const [departmentsList, setDepartmentsList] = useState<
        { id: number; name: string }[]
    >([]);

    const methods = useForm({
        defaultValues,
    });
    const {
        control,
        setValue,
        getValues,
        watch,
        reset,
        formState: { errors, isSubmitting },
    } = methods;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'contacts',
    });
    const { fields: departmentFields, replace } = useFieldArray({
        control,
        name: 'departments',
    });

    const fetchUserRoles = async () => {
        try {
            const res = await fetchRoles();
            const excluded_roles = [
                'patient',
                'medical professional',
                'full team member',
                'limited team member',
                'no access team member',
                'emergency_contact',
            ];
            setRoles(
                res.filter(
                    (role: UserRoleType) => !excluded_roles.includes(role.name),
                ),
            );
        } catch (error) {
            console.log(error);
        }
    };

    // Departments come from the API rather than a literal in this file: the
    // literal had drifted from the table, and any name it got wrong was
    // silently dropped on save (HHLTB-2665).
    const fetchDepartmentsList = async () => {
        try {
            setDepartmentsList(await fetchDepartments());
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    useEffect(() => {
        fetchUserRoles();
        fetchDepartmentsList();
    }, []);

    useEffect(() => {
        if (user) {
            const formattedPhones = user.person?.phone_set?.map((u) => {
                return { phone: u.phone || '', type: u.type || '' };
            });

            // The API sends null rather than [] for a user in no departments.
            const userDepartments = user.departments || [];

            reset({
                first_name: user.person.first_name,
                middle_name: user.person.middle_name,
                last_name: user.person.last_name,
                contacts: formattedPhones,
                email: user.person.email,
                roles: user.roles,
                departments: userDepartments,
            });
            replace(userDepartments);
        } else {
            // Reset form when adding a new user
            reset(defaultValues);
        }
    }, [user, reset, replace]);

    const onDepartmentSelect = (departmentName: string, role: string) => {
        const currentDepartments = departmentFields;

        const existingIndex = currentDepartments.findIndex(
            (dept) => dept.name === departmentName,
        );

        if (existingIndex >= 0) {
            if (currentDepartments[existingIndex].role === role) {
                currentDepartments.splice(existingIndex, 1);
            } else {
                currentDepartments[existingIndex].role = role;
            }
        } else {
            currentDepartments.push({
                name: departmentName,
                role,
                id: uuidv4(),
            });
        }

        replace(currentDepartments);
    };

    const onSubmit = async (data: any) => {
        try {
            // Transform contacts to phone_set expected by the API
            const formattedData = {
                ...data,
                phone_set: data.contacts.map((contact: Contact) => ({
                    phone: contact.phone,
                    type: contact.type,
                })),
            };

            delete formattedData.contacts;

            console.log(formattedData);
            let updatedUser;

            if (user?.id) {
                // Update existing user
                updatedUser = await updateUser(user.id, formattedData);
            } else {
                // Create new user
                updatedUser = await createUser(formattedData);
            }

            if (onSuccess) {
                onSuccess(updatedUser);
            }

            setIsOpen(false);
        } catch (error: any) {
            toast.dismiss();
            console.error('Error submitting form:', error);
            toast.error(
                error?.message ||
                    `Error ${
                        user?.id ? 'updating' : 'creating'
                    } user. Please try again.`,
            );
        }
    };

    return (
        <>
            <Drawer
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                title={user?.id ? 'Edit User' : 'Add User'}
            >
                <FormProvider {...methods}>
                    <form
                        onSubmit={methods.handleSubmit((data) =>
                            onSubmit(data),
                        )}
                    >
                        <h2 className={sectionStyles}>CONTACT</h2>
                        <label htmlFor='cid-first-name' className={labelStyles}>
                            First Name<span className={requiredStyles}>*</span>
                        </label>
                        <Controller
                            name='first_name'
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <input
                                    id='cid-first-name'
                                    {...field}
                                    className={`${inputStyles} w-full`}
                                />
                            )}
                        />
                        {errors.first_name && (
                            <ErrorMessage message='This field is required' />
                        )}

                        <label
                            htmlFor='cid-middle_name'
                            className={labelStyles}
                        >
                            Middle Name
                        </label>
                        <Controller
                            name='middle_name'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='cid-middle_name'
                                    {...field}
                                    className={`${inputStyles} w-full`}
                                />
                            )}
                        />

                        <label htmlFor='cid-last_name' className={labelStyles}>
                            Last Name<span className={requiredStyles}>*</span>
                        </label>
                        <Controller
                            name='last_name'
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <input
                                    id='cid-last_name'
                                    {...field}
                                    className={`${inputStyles} w-full`}
                                />
                            )}
                        />
                        {errors.last_name && (
                            <ErrorMessage message='This field is required' />
                        )}
                        {fields.map((contact, index) => {
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                    }}
                                    key={contact.id}
                                >
                                    <div className='w-40'>
                                        <label
                                            htmlFor={`contacts[${index}].phone`}
                                            className={labelStyles}
                                        >
                                            Contact Number
                                        </label>
                                        <Controller
                                            name={`contacts.${index}.phone`}
                                            control={control}
                                            rules={{
                                                required: true,
                                                pattern: {
                                                    value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                                    message:
                                                        'Please enter a valid phone number in format (123) 456-7890',
                                                },
                                            }}
                                            render={({ field }) => (
                                                <input
                                                    id={`contacts[${index}].phone`}
                                                    type='tel'
                                                    placeholder='(123) 456-7890'
                                                    maxLength={14}
                                                    onChange={(e) => {
                                                        // Remove all non-digits
                                                        const digits =
                                                            e.target.value.replace(
                                                                /\D/g,
                                                                '',
                                                            );

                                                        // Format as (XXX) XXX-XXXX
                                                        let formatted = '';
                                                        if (
                                                            digits.length >= 1
                                                        ) {
                                                            formatted = `(${digits.slice(
                                                                0,
                                                                3,
                                                            )}`;
                                                        }
                                                        if (
                                                            digits.length >= 4
                                                        ) {
                                                            formatted += `) ${digits.slice(
                                                                3,
                                                                6,
                                                            )}`;
                                                        }
                                                        if (
                                                            digits.length >= 7
                                                        ) {
                                                            formatted += `-${digits.slice(
                                                                6,
                                                                10,
                                                            )}`;
                                                        }

                                                        field.onChange(
                                                            formatted,
                                                        );
                                                    }}
                                                    value={field.value}
                                                    className={`${inputStyles} w-full`}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className='w-30'>
                                        <label
                                            htmlFor={`contacts.${index}.type`}
                                            className={`${labelStyles} w-full grow`}
                                        >
                                            Type
                                            <span className={requiredStyles}>
                                                *
                                            </span>
                                        </label>
                                        <Controller
                                            control={control}
                                            name={`contacts.${index}.type`}
                                            render={({ field }) => (
                                                <>
                                                    <select
                                                        id={`contacts.${index}.type`}
                                                        className={`${inputStyles} w-full bg-transparent`}
                                                        {...field}
                                                    >
                                                        <option
                                                            value=''
                                                            disabled
                                                        >
                                                            Select
                                                        </option>
                                                        {phoneNumberTypesList.map(
                                                            (t, i) => {
                                                                return (
                                                                    <option
                                                                        value={
                                                                            t
                                                                        }
                                                                        key={`phone_${i}`}
                                                                    >
                                                                        {t}
                                                                    </option>
                                                                );
                                                            },
                                                        )}
                                                    </select>
                                                </>
                                            )}
                                        />
                                    </div>
                                    {index !== 0 && (
                                        <div className='mt-[22px]'>
                                            <RiDeleteBin6Line
                                                color='#FF0000'
                                                size={'20px'}
                                                onClick={() => {
                                                    remove(index);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <button
                            type='button'
                            className='mt-3 flex items-center gap-1 text-sm font-medium text-[#00aeb3]'
                            onClick={() => {
                                append({
                                    phone: '',
                                    type: PhoneNumberTypes.OFFICE,
                                });
                            }}
                        >
                            <FaPlus />
                            ADD PHONE NUMBER
                        </button>

                        <label htmlFor='cid-email' className={labelStyles}>
                            Email
                        </label>
                        <Controller
                            name='email'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='cid-email'
                                    {...field}
                                    className={`${inputStyles} w-full`}
                                />
                            )}
                        />

                        <h2 className={sectionStyles}>ROLE</h2>

                        <label
                            htmlFor={`roles`}
                            className={`${labelStyles} w-full grow`}
                        >
                            Roles<span className={requiredStyles}>*</span>
                        </label>
                        <Controller
                            control={control}
                            name={`roles`}
                            render={({ field }) => (
                                <>
                                    <Select
                                        menuPlacement='auto'
                                        isMulti
                                        name='roles'
                                        options={roles.map((role) => ({
                                            value: role.name,
                                            label: startCase(role.name),
                                        }))}
                                        onChange={(selectedOptions) => {
                                            field.onChange(
                                                selectedOptions
                                                    ? selectedOptions.map(
                                                          (option) =>
                                                              option.value,
                                                      )
                                                    : [],
                                            );
                                        }}
                                        value={
                                            field.value
                                                ? field.value.map(
                                                      (role: string) => ({
                                                          value: role,
                                                          label: startCase(
                                                              role,
                                                          ),
                                                      }),
                                                  )
                                                : []
                                        }
                                        className='w-full'
                                    />
                                </>
                            )}
                        />

                        <h2 className={sectionStyles}>DEPARTMENTS</h2>

                        {departmentsList.map((dept) => {
                            const deptFound = departmentFields.find(
                                (dep) => dept.name === dep.name,
                            );

                            return (
                                <div
                                    key={dept.name}
                                    className='mt-2 flex flex-wrap items-center justify-between border-b border-[#e5e5e5] pb-4'
                                >
                                    <h3
                                        className={`w-[160px] text-sm  ${
                                            deptFound && 'font-semibold'
                                        }`}
                                    >
                                        {dept.name}
                                    </h3>
                                    <div className='flex space-x-4'>
                                        {departmentRoles.map((option) => {
                                            return (
                                                <Controller
                                                    key={option}
                                                    name={`departments`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <label
                                                            className={`cursor-pointer rounded-full border px-3 py-1 text-sm ${
                                                                deptFound?.role ===
                                                                option
                                                                    ? 'border-[#00aeb3] bg-[#00aeb3] text-black'
                                                                    : 'bg-white text-black'
                                                            }`}
                                                        >
                                                            <input
                                                                type='radio'
                                                                value={option}
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    field.onChange(
                                                                        e.target
                                                                            .value,
                                                                    );
                                                                    onDepartmentSelect(
                                                                        dept.name,
                                                                        e.target
                                                                            .value,
                                                                    );
                                                                }}
                                                                onClick={() => {
                                                                    if (
                                                                        deptFound?.role ===
                                                                        option
                                                                    ) {
                                                                        onDepartmentSelect(
                                                                            dept.name,
                                                                            option,
                                                                        );
                                                                    }
                                                                }}
                                                                checked={
                                                                    deptFound?.role ===
                                                                    option
                                                                        ? true
                                                                        : false
                                                                }
                                                                className='hidden'
                                                            />
                                                            {option}
                                                        </label>
                                                    )}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}

                        <div className='mt-[30px] flex flex-wrap gap-[16px] '>
                            <button
                                type='button'
                                onClick={() => setIsOpen(false)}
                                className=' rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type='submit'
                                className='w-22 rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                disabled={isSubmitting}
                            >
                                {isSubmitting
                                    ? 'Saving...'
                                    : user?.id
                                    ? 'Update'
                                    : 'Create'}
                            </button>
                        </div>
                    </form>
                </FormProvider>
            </Drawer>
        </>
    );
};

export default AddUserDrawer;
